import {
  checkTemplate,
  templateVariables,
  type TemplateButton,
  type TemplateInput,
  type TemplateIssue,
} from '@ly360/channels/whatsapp-manager';
import { Dialog, Empty, ErrorState, Loading, PageHead, Select, useFormat } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ExternalLink,
  Languages,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  Reply,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { Field } from '../crm/forms';
import { api, upload } from '../lib/api';
import { DialogButtons } from '../retail/ui';

/**
 * WhatsApp Manager: for each WhatsApp number, its health with Meta (display name review, quality, messaging
 * limit, business verification, restrictions), what it sent, Meta's notices, and its message templates: built
 * here with a live preview, submitted for review, edited, translated, deleted, or started from the library.
 */
interface Account {
  id: string;
  displayName: string;
  connection: string;
  phoneNumberId: string | null;
  displayPhoneNumber: string | null;
  verifiedName: string | null;
  nameStatus: string | null;
  quality: 'green' | 'yellow' | 'red' | 'unknown' | null;
  messagingLimit: string | null;
  status: string | null;
  mode: string | null;
  businessName: string | null;
  businessReview: string | null;
  businessVerification: string | null;
  checkedAt: string | null;
  lastError: string | null;
  templates: Record<string, number> | null;
  usage: { category: string; sent: number; failed: number }[];
}
interface Notice {
  id: string;
  accountId: string;
  kind: string;
  detail: Record<string, unknown>;
  at: string;
}
interface Template {
  id: string;
  name: string;
  language: string;
  category: 'marketing' | 'utility' | 'authentication';
  body: string;
  status: 'draft' | 'pending' | 'approved' | 'rejected' | 'paused' | 'disabled' | 'in_appeal';
  externalId: string | null;
  components: {
    header?: TemplateInput['header'];
    footer?: string | null;
    buttons?: TemplateButton[];
    examples?: string[];
  };
  rejectedReason: string | null;
  quality: 'green' | 'yellow' | 'red' | 'unknown';
  source: 'ly360' | 'meta' | 'manual';
  updatedAt: string;
}
type LibraryItem = TemplateInput & { use: string };

const LIMITS: Record<string, string> = { '250': '250', '1k': '1,000', '10k': '10,000', '100k': '100,000' };

export function WhatsAppPage() {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ['whatsapp'],
    queryFn: () => api<{ accounts: Account[]; events: Notice[] }>('/v1/whatsapp'),
  });
  const [active, setActive] = useState<string | null>(null);
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState error={q.error} />;
  const accounts = q.data.accounts;
  const a = accounts.find((x) => x.id === active) ?? accounts[0];
  return (
    <>
      <PageHead eyebrow={t('nav.admin')} title={t('whatsapp.title')} subtitle={t('whatsapp.subtitle')} />
      {!a ? (
        <section className="card card-pad">
          <Empty text={t('whatsapp.none')} />
          <p style={{ textAlign: 'center' }}>
            <Link to="/admin/integrations" className="btn btn-grad">
              {t('whatsapp.connect')}
            </Link>
          </p>
        </section>
      ) : (
        <>
          {accounts.length > 1 && (
            <div className="segmented" role="tablist" style={{ marginBottom: '1rem' }}>
              {accounts.map((x) => (
                <button key={x.id} role="tab" aria-pressed={x.id === a.id} onClick={() => setActive(x.id)}>
                  {x.displayPhoneNumber ?? x.displayName}
                </button>
              ))}
            </div>
          )}
          <SenderCard a={a} notices={q.data.events.filter((e) => e.accountId === a.id)} />
          <Templates account={a} />
        </>
      )}
    </>
  );
}

// ------------------------------------------------------------------------------------------- the number

function SenderCard({ a, notices }: { a: Account; notices: Notice[] }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const refresh = useMutation({
    mutationFn: () => api(`/v1/whatsapp/${a.id}/refresh`, { method: 'POST' }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['whatsapp'] });
      void qc.invalidateQueries({ queryKey: ['wa-templates', a.id] });
    },
  });
  const tpl = a.templates ?? {};
  // What needs attention, most serious first, each with what to do.
  const attention: string[] = [];
  if (a.status && ['restricted', 'banned', 'disabled'].includes(a.status))
    attention.push(t('whatsapp.advice.restricted'));
  if (a.status === 'flagged') attention.push(t('whatsapp.advice.flagged'));
  if (a.quality === 'red') attention.push(t('whatsapp.advice.red'));
  else if (a.quality === 'yellow') attention.push(t('whatsapp.advice.yellow'));
  if (a.nameStatus === 'declined') attention.push(t('whatsapp.advice.nameDeclined'));
  if (a.businessVerification && a.businessVerification !== 'verified')
    attention.push(t('whatsapp.advice.verifyBusiness'));
  if (tpl.rejected) attention.push(t('whatsapp.advice.rejected', { count: tpl.rejected }));
  if (a.mode === 'sandbox') attention.push(t('whatsapp.advice.sandboxMode'));
  if (a.lastError) attention.push(t('whatsapp.advice.unreachable', { error: a.lastError }));
  const sent = a.usage.reduce((s, u) => s + u.sent, 0);

  return (
    <section className="card card-pad wa-sender" data-testid="wa-sender">
      <div className="wa-sender-head">
        <span className="wa-avatar" aria-hidden="true">
          <Phone size={18} />
        </span>
        <div className="grow">
          <h2 className="section-title" style={{ margin: 0 }} dir="auto">
            {a.verifiedName ?? a.displayName}
          </h2>
          <span className="faint mono" dir="ltr">
            {a.displayPhoneNumber ?? a.phoneNumberId}
          </span>
        </div>
        <StatusPill value={a.status} />
        {a.mode === 'sandbox' && <span className="badge badge-warning">{t('whatsapp.mode.sandbox')}</span>}
        <button
          className="btn btn-sm"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
          data-testid="wa-refresh"
        >
          <RefreshCw size={14} className={refresh.isPending ? 'spin' : undefined} />
          {t('whatsapp.refresh')}
        </button>
      </div>
      {refresh.isError && <ErrorState error={refresh.error} />}
      <dl className="wa-health">
        <div>
          <dt>{t('whatsapp.health.quality')}</dt>
          <dd data-testid="wa-quality">
            <span className={`quality-dot q-${a.quality ?? 'unknown'}`} aria-hidden="true" />
            {t(`whatsapp.quality.${a.quality ?? 'unknown'}`)}
          </dd>
          <span className="faint">{t('whatsapp.health.qualityHint')}</span>
        </div>
        <div>
          <dt>{t('whatsapp.health.limit')}</dt>
          <dd data-testid="wa-limit">
            {a.messagingLimit
              ? a.messagingLimit === 'unlimited'
                ? t('whatsapp.limit.unlimited')
                : t('whatsapp.limit.perDay', { n: LIMITS[a.messagingLimit] ?? a.messagingLimit })
              : '—'}
          </dd>
          <span className="faint">{t('whatsapp.health.limitHint')}</span>
        </div>
        <div>
          <dt>{t('whatsapp.health.name')}</dt>
          <dd>
            {a.nameStatus ? t(`whatsapp.nameStatus.${a.nameStatus}`, { defaultValue: a.nameStatus }) : '—'}
          </dd>
          <span className="faint">{t('whatsapp.health.nameHint')}</span>
        </div>
        <div>
          <dt>{t('whatsapp.health.business')}</dt>
          <dd>
            {a.businessVerification
              ? t(`whatsapp.verification.${a.businessVerification}`, { defaultValue: a.businessVerification })
              : '—'}
          </dd>
          <span className="faint" dir="auto">
            {a.businessName ?? ''}
          </span>
        </div>
        <div>
          <dt>{t('whatsapp.health.templates')}</dt>
          <dd>{t('whatsapp.templateCounts', { approved: tpl.approved ?? 0, pending: tpl.pending ?? 0 })}</dd>
          {!!tpl.rejected && (
            <span className="faint">{t('whatsapp.rejectedCount', { count: tpl.rejected })}</span>
          )}
        </div>
      </dl>
      <p className="faint" style={{ fontSize: 'var(--t-xs)', margin: 0 }}>
        {a.checkedAt ? t('whatsapp.checked', { rel: fmt.relative(a.checkedAt) }) : t('whatsapp.neverChecked')}
      </p>

      {attention.length > 0 && (
        <div className="notice warn wa-attention" role="status" data-testid="wa-attention">
          <AlertTriangle size={16} aria-hidden="true" />
          <ul className="list-plain">
            {attention.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="wa-columns">
        <div>
          <h3 className="wa-sub">{t('whatsapp.usage.title')}</h3>
          {sent === 0 ? (
            <p className="faint">{t('whatsapp.usage.none')}</p>
          ) : (
            <ul className="list-plain wa-usage" data-testid="wa-usage">
              {a.usage.map((u) => (
                <li key={u.category}>
                  <span className="grow">
                    {t(`whatsapp.category.${u.category}`, { defaultValue: u.category })}
                  </span>
                  <span
                    className="bar"
                    style={{ ['--w' as string]: `${Math.max(4, (u.sent / sent) * 100)}%` }}
                    aria-hidden="true"
                  />
                  <span className="num">{fmt.number(u.sent)}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="faint" style={{ fontSize: 'var(--t-xs)' }}>
            {t('whatsapp.usage.note')}
          </p>
        </div>
        <div>
          <h3 className="wa-sub">{t('whatsapp.notices.title')}</h3>
          {notices.length === 0 ? (
            <p className="faint">{t('whatsapp.notices.none')}</p>
          ) : (
            <ul className="list-plain wa-notices" data-testid="wa-notices">
              {notices.slice(0, 8).map((n) => (
                <li key={n.id}>
                  <span className="grow">{noticeText(t, n)}</span>
                  <time className="faint" dateTime={n.at} title={fmt.dateTime(n.at)}>
                    {fmt.relative(n.at)}
                  </time>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function noticeText(t: ReturnType<typeof useTranslation>['t'], n: Notice): string {
  const d = n.detail as Record<string, string>;
  switch (n.kind) {
    case 'template_status':
      return t(`whatsapp.notice.tpl.${d.status}`, {
        name: d.name,
        language: d.language,
        reason: d.reason ? t(`whatsapp.reason.${d.reason}`, { defaultValue: d.reason }) : '',
        defaultValue: t('whatsapp.notice.tplChanged', { name: d.name, status: d.status }),
      });
    case 'template_category':
      return t('whatsapp.notice.template_category', {
        name: d.name,
        category: t(`whatsapp.category.${d.category}`, { defaultValue: d.category }),
      });
    case 'template_quality':
      return t('whatsapp.notice.template_quality', {
        name: d.name,
        quality: t(`whatsapp.quality.${d.quality}`),
      });
    case 'quality':
      return t('whatsapp.notice.quality', { quality: t(`whatsapp.quality.${d.to}`) });
    case 'messaging_limit':
      return t('whatsapp.notice.limit', { n: LIMITS[d.to ?? ''] ?? d.to });
    case 'status':
      return t('whatsapp.notice.status', { status: t(`whatsapp.status.${d.to}`, { defaultValue: d.to }) });
    case 'name':
      return t('whatsapp.notice.name', { status: t(`whatsapp.nameStatus.${d.to}`, { defaultValue: d.to }) });
    default:
      return t('whatsapp.notice.account', { event: String(d.event ?? n.kind) });
  }
}

function StatusPill({ value }: { value: string | null }) {
  const { t } = useTranslation();
  if (!value) return null;
  const bad = ['restricted', 'banned', 'disabled', 'flagged'].includes(value);
  return (
    <span className={`badge ${bad ? 'badge-danger' : 'badge-success'}`} data-testid="wa-status">
      {bad ? <AlertTriangle size={12} /> : <CheckCircle2 size={12} />}
      {t(`whatsapp.status.${value}`, { defaultValue: value })}
    </span>
  );
}

// ------------------------------------------------------------------------------------------- templates

function Templates({ account }: { account: Account }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState<'all' | 'approved' | 'pending' | 'rejected' | 'draft'>('all');
  const [editing, setEditing] = useState<{ template?: Template; preset?: Partial<TemplateInput> } | null>(
    null,
  );
  const [library, setLibrary] = useState(false);
  const q = useQuery({
    queryKey: ['wa-templates', account.id],
    queryFn: () => api<{ items: Template[] }>(`/v1/whatsapp/${account.id}/templates`),
    // Follow Meta's review while something is waiting.
    refetchInterval: (query) => (query.state.data?.items.some((x) => x.status === 'pending') ? 5000 : false),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/v1/whatsapp/templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['wa-templates', account.id] });
      void qc.invalidateQueries({ queryKey: ['whatsapp'] });
    },
  });
  // "Create it" links from Automations: /admin/whatsapp?create=order_shipped
  const wanted = params.get('create');
  useEffect(() => {
    if (wanted) {
      setEditing({ preset: { name: wanted } });
      params.delete('create');
      setParams(params, { replace: true });
    }
  }, [wanted, params, setParams]);

  const items = q.data?.items ?? [];
  const groups = useMemo(() => {
    const m = new Map<string, Template[]>();
    for (const x of items)
      if (filter === 'all' || x.status === filter) m.set(x.name, [...(m.get(x.name) ?? []), x]);
    return [...m.entries()];
  }, [items, filter]);

  return (
    <section className="card card-pad" data-testid="wa-templates">
      <div className="card-head-row">
        <h2 className="section-title" style={{ margin: 0 }}>
          {t('whatsapp.templates.title')}
        </h2>
        <div className="wa-toolbar">
          <Select
            value={filter}
            onChange={(v) => setFilter(v as typeof filter)}
            aria-label={t('whatsapp.templates.filter')}
            options={(['all', 'approved', 'pending', 'rejected', 'draft'] as const).map((s) => ({
              value: s,
              label: t(`whatsapp.templates.show.${s}`),
            }))}
          />
          <button className="btn btn-sm" onClick={() => setLibrary(true)} data-testid="wa-library">
            <BookOpen size={14} />
            {t('whatsapp.templates.library')}
          </button>
          <button
            className="btn btn-sm btn-grad"
            onClick={() => setEditing({})}
            data-testid="wa-new-template"
          >
            <Plus size={14} />
            {t('whatsapp.templates.new')}
          </button>
        </div>
      </div>
      <p className="faint" style={{ marginTop: '0.25rem' }}>
        {t('whatsapp.templates.hint')}
      </p>
      {q.isLoading && <Loading />}
      {q.data && !groups.length && <Empty text={t('whatsapp.templates.empty')} />}
      <ul className="list-plain wa-template-list">
        {groups.map(([name, langs]) => (
          <li key={name} data-testid={`wa-template-${name}`}>
            <div className="wa-template-head">
              <strong className="mono">{name}</strong>
              <span
                className={`badge ${langs[0]!.category === 'marketing' ? 'badge-warning' : 'badge-neutral'}`}
              >
                {t(`whatsapp.category.${langs[0]!.category}`)}
              </span>
              {langs[0]!.source === 'manual' && (
                <span className="faint">{t('whatsapp.templates.byHand')}</span>
              )}
              <span className="grow" />
              <button
                className="btn btn-ghost btn-sm"
                onClick={() =>
                  setEditing({
                    preset: {
                      ...toInput(langs[0]!),
                      language: langs[0]!.language === 'ar' ? 'en' : 'ar',
                      body: '',
                    },
                  })
                }
              >
                <Languages size={14} />
                {t('whatsapp.templates.addLanguage')}
              </button>
            </div>
            <ul className="list-plain wa-langs">
              {langs.map((x) => (
                <li key={x.id}>
                  <span className="badge badge-neutral mono">{x.language}</span>
                  <TemplateStatus value={x.status} />
                  {x.quality !== 'unknown' && x.status === 'approved' && (
                    <span className="faint" title={t('whatsapp.health.quality')}>
                      <span className={`quality-dot q-${x.quality}`} aria-hidden="true" />{' '}
                      {t(`whatsapp.quality.${x.quality}`)}
                    </span>
                  )}
                  <span className="grow ellipsis faint" dir="auto">
                    {x.body}
                  </span>
                  {x.status !== 'pending' && x.status !== 'in_appeal' && (
                    <button
                      className="icon-btn"
                      aria-label={t('common.edit')}
                      onClick={() => setEditing({ template: x })}
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                  <button
                    className="icon-btn"
                    aria-label={t('common.delete')}
                    onClick={() =>
                      window.confirm(
                        t('whatsapp.templates.deleteConfirm', { name: x.name, language: x.language }),
                      ) && remove.mutate(x.id)
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                  {x.rejectedReason && (
                    <p className="notice warn wa-reason" role="status">
                      {t('whatsapp.templates.rejectedBecause', {
                        reason: t(`whatsapp.reason.${x.rejectedReason}`, { defaultValue: x.rejectedReason }),
                      })}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
      {editing && (
        <TemplateBuilder
          accountId={account.id}
          template={editing.template}
          preset={editing.preset}
          onClose={() => setEditing(null)}
        />
      )}
      {library && (
        <LibraryDialog
          accountId={account.id}
          existing={items}
          onUse={(p) => {
            setLibrary(false);
            setEditing({ preset: p });
          }}
          onClose={() => setLibrary(false)}
        />
      )}
    </section>
  );
}

function TemplateStatus({ value }: { value: Template['status'] }) {
  const { t } = useTranslation();
  const cls =
    value === 'approved'
      ? 'badge-success'
      : value === 'rejected' || value === 'disabled'
        ? 'badge-danger'
        : value === 'draft'
          ? 'badge-neutral'
          : 'badge-warning';
  return (
    <span className={`badge ${cls}`} data-testid="wa-template-status">
      {t(`whatsapp.templateStatus.${value}`)}
    </span>
  );
}

const toInput = (x: Template): TemplateInput => ({
  name: x.name,
  language: x.language,
  category: x.category,
  header: x.components.header ?? null,
  body: x.body,
  examples: x.components.examples ?? [],
  footer: x.components.footer ?? null,
  buttons: x.components.buttons ?? [],
});

// ----------------------------------------------------------------------------------------------- builder

function TemplateBuilder({
  accountId,
  template,
  preset,
  onClose,
}: {
  accountId: string;
  template?: Template;
  preset?: Partial<TemplateInput>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [f, setF] = useState<TemplateInput>(() => ({
    name: '',
    language: 'en',
    category: 'utility',
    header: null,
    body: '',
    examples: [],
    footer: null,
    buttons: [],
    ...(template ? toInput(template) : {}),
    ...(preset ?? {}),
  }));
  const set = (p: Partial<TemplateInput>) => setF((x) => ({ ...x, ...p }));
  const vars = [...new Set(templateVariables(f.body))].sort((a, b) => a - b);
  const issues: TemplateIssue[] = checkTemplate({ ...f, examples: vars.map((_, i) => f.examples[i] ?? '') });
  const save = useMutation({
    mutationFn: (opts: { draft: boolean }) => {
      const body = { ...f, examples: vars.map((_, i) => f.examples[i] ?? '') };
      return template
        ? api(`/v1/whatsapp/templates/${template.id}`, {
            method: 'PATCH',
            body: JSON.stringify({ ...body, submit: !opts.draft }),
          })
        : api(`/v1/whatsapp/${accountId}/templates`, {
            method: 'POST',
            body: JSON.stringify({ ...body, draft: opts.draft }),
          });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['wa-templates', accountId] });
      void qc.invalidateQueries({ queryKey: ['whatsapp'] });
      onClose();
    },
  });
  const addVariable = () =>
    set({ body: `${f.body}${f.body && !f.body.endsWith(' ') ? ' ' : ''}{{${vars.length + 1}}}` });
  const media = async (file: File) => {
    const r = await upload<{ url: string }>(`/v1/media?name=${encodeURIComponent(file.name)}`, file);
    const format = file.type.startsWith('video/')
      ? 'video'
      : file.type.startsWith('image/')
        ? 'image'
        : 'document';
    set({ header: { format, mediaUrl: r.url } });
  };
  const headerKind = f.header?.format ?? 'none';

  return (
    <Dialog
      open
      wide
      title={template ? t('whatsapp.builder.edit', { name: template.name }) : t('whatsapp.builder.new')}
      onClose={onClose}
      onSubmit={() => save.mutate({ draft: false })}
      testId="wa-builder"
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          {(!template || template.status === 'draft') && (
            <button
              type="button"
              className="btn"
              disabled={save.isPending}
              onClick={() => save.mutate({ draft: true })}
              data-testid="wa-save-draft"
            >
              {t('whatsapp.builder.saveDraft')}
            </button>
          )}
          <button
            type="submit"
            className="btn btn-grad"
            disabled={save.isPending || issues.length > 0}
            data-testid="wa-submit"
          >
            {t('whatsapp.builder.submit')}
          </button>
        </>
      }
    >
      <div className="wa-builder">
        <div className="wa-form">
          <div className="form-grid">
            <Field id="wt-name" label={t('whatsapp.builder.name')} hint={t('whatsapp.builder.nameHint')}>
              <input
                id="wt-name"
                className="input mono"
                dir="ltr"
                value={f.name}
                disabled={!!template}
                onChange={(e) => set({ name: e.target.value.toLowerCase().replace(/[^a-z0-9_]+/g, '_') })}
                data-testid="wt-name"
              />
            </Field>
            <Field id="wt-lang" label={t('whatsapp.builder.language')}>
              <Select
                id="wt-lang"
                value={f.language}
                onChange={(v) => set({ language: v })}
                options={['en', 'ar', 'en_US', 'en_GB', 'hi', 'ml', 'ta', 'ur', 'fil', 'fr'].map((l) => ({
                  value: l,
                  label: t(`whatsapp.languages.${l}`, { defaultValue: l }),
                }))}
              />
            </Field>
          </div>
          <Field
            id="wt-cat"
            label={t('whatsapp.builder.category')}
            hint={t(`whatsapp.categoryHint.${f.category}`)}
          >
            <div className="segmented" role="group" aria-label={t('whatsapp.builder.category')}>
              {(['utility', 'marketing'] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-pressed={f.category === c}
                  onClick={() => set({ category: c })}
                >
                  {t(`whatsapp.category.${c}`)}
                </button>
              ))}
            </div>
          </Field>
          <Field id="wt-header" label={t('whatsapp.builder.header')}>
            <div className="segmented" role="group" aria-label={t('whatsapp.builder.header')}>
              {(['none', 'text', 'image', 'video', 'document'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  aria-pressed={headerKind === k}
                  onClick={() =>
                    set({
                      header:
                        k === 'none'
                          ? null
                          : k === 'text'
                            ? { format: 'text', text: '' }
                            : { format: k, mediaUrl: '' },
                    })
                  }
                >
                  {t(`whatsapp.builder.headerKind.${k}`)}
                </button>
              ))}
            </div>
          </Field>
          {f.header?.format === 'text' && (
            <input
              className="input"
              dir="auto"
              maxLength={60}
              value={f.header.text}
              onChange={(e) => set({ header: { format: 'text', text: e.target.value } })}
              aria-label={t('whatsapp.builder.headerText')}
              placeholder={t('whatsapp.builder.headerText')}
            />
          )}
          {f.header && f.header.format !== 'text' && (
            <label className="btn btn-sm" style={{ justifySelf: 'start' }}>
              {f.header.mediaUrl ? t('whatsapp.builder.replaceMedia') : t('whatsapp.builder.uploadMedia')}
              <input
                type="file"
                hidden
                accept={
                  f.header.format === 'image'
                    ? 'image/jpeg,image/png'
                    : f.header.format === 'video'
                      ? 'video/mp4'
                      : 'application/pdf'
                }
                onChange={(e) => e.target.files?.[0] && void media(e.target.files[0])}
              />
            </label>
          )}
          <Field id="wt-body" label={t('whatsapp.builder.body')} hint={t('whatsapp.builder.bodyHint')}>
            <textarea
              id="wt-body"
              className="textarea"
              rows={5}
              dir="auto"
              maxLength={1024}
              value={f.body}
              onChange={(e) => set({ body: e.target.value })}
              data-testid="wt-body"
            />
          </Field>
          <button type="button" className="btn btn-sm" onClick={addVariable} style={{ justifySelf: 'start' }}>
            <Plus size={13} />
            {t('whatsapp.builder.addVariable')}
          </button>
          {vars.length > 0 && (
            <div className="wa-examples">
              <span className="as-label">{t('whatsapp.builder.examples')}</span>
              {vars.map((v, i) => (
                <label key={v} className="wa-example">
                  <span className="mono faint">{`{{${v}}}`}</span>
                  <input
                    className="input"
                    dir="auto"
                    value={f.examples[i] ?? ''}
                    onChange={(e) => {
                      const ex = [...f.examples];
                      ex[i] = e.target.value;
                      set({ examples: ex });
                    }}
                    data-testid={`wt-example-${v}`}
                  />
                </label>
              ))}
            </div>
          )}
          <Field id="wt-footer" label={t('whatsapp.builder.footer')}>
            <input
              id="wt-footer"
              className="input"
              dir="auto"
              maxLength={60}
              value={f.footer ?? ''}
              onChange={(e) => set({ footer: e.target.value || null })}
            />
          </Field>
          <div className="wa-buttons">
            <span className="as-label">{t('whatsapp.builder.buttons')}</span>
            {(f.buttons ?? []).map((b, i) => (
              <div key={i} className="wa-button-row">
                <span className="badge badge-neutral">{t(`whatsapp.builder.buttonKind.${b.type}`)}</span>
                <input
                  className="input"
                  dir="auto"
                  maxLength={25}
                  value={b.text}
                  aria-label={t('whatsapp.builder.buttonText')}
                  onChange={(e) =>
                    set({ buttons: f.buttons!.map((x, k) => (k === i ? { ...x, text: e.target.value } : x)) })
                  }
                />
                {b.type === 'url' && (
                  <input
                    className="input"
                    dir="ltr"
                    value={b.url}
                    placeholder="https://"
                    aria-label="URL"
                    onChange={(e) =>
                      set({
                        buttons: f.buttons!.map((x, k) => (k === i ? { ...b, url: e.target.value } : x)),
                      })
                    }
                  />
                )}
                {b.type === 'phone' && (
                  <input
                    className="input"
                    dir="ltr"
                    value={b.phone}
                    placeholder="+974…"
                    aria-label={t('whatsapp.builder.buttonKind.phone')}
                    onChange={(e) =>
                      set({
                        buttons: f.buttons!.map((x, k) => (k === i ? { ...b, phone: e.target.value } : x)),
                      })
                    }
                  />
                )}
                <button
                  type="button"
                  className="icon-btn"
                  aria-label={t('common.delete')}
                  onClick={() => set({ buttons: f.buttons!.filter((_, k) => k !== i) })}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
            <div className="chips">
              {(['quick_reply', 'url', 'phone'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  className="chip chip-button"
                  onClick={() =>
                    set({
                      buttons: [
                        ...(f.buttons ?? []),
                        k === 'quick_reply'
                          ? { type: k, text: '' }
                          : k === 'url'
                            ? { type: k, text: '', url: 'https://' }
                            : { type: k, text: '', phone: '+' },
                      ],
                    })
                  }
                >
                  <Plus size={12} /> {t(`whatsapp.builder.buttonKind.${k}`)}
                </button>
              ))}
            </div>
          </div>
          {issues.length > 0 && (
            <ul className="list-plain wa-issues" data-testid="wa-issues">
              {issues.map((i, k) => (
                <li key={k} className="faint">
                  <AlertTriangle size={12} /> {t(`whatsapp.issue.${i.code}`, { max: i.max })}
                </li>
              ))}
            </ul>
          )}
          {save.isError && <ErrorState error={save.error} />}
        </div>
        <Preview f={f} />
      </div>
    </Dialog>
  );
}

/** How the message looks on the customer's phone, with the examples filled in. */
function Preview({ f }: { f: TemplateInput }) {
  const { t } = useTranslation();
  const body = f.body.replace(/\{\{(\d+)\}\}/g, (_m, n: string) => f.examples[Number(n) - 1] || `{{${n}}}`);
  return (
    <div className="wa-preview" aria-label={t('whatsapp.builder.preview')} data-testid="wa-preview">
      <span className="as-label">{t('whatsapp.builder.preview')}</span>
      <div className="wa-phone">
        <div className="wa-bubble">
          {f.header?.format === 'text' && f.header.text && <strong dir="auto">{f.header.text}</strong>}
          {f.header && f.header.format !== 'text' && (
            <div className="wa-media">
              {f.header.format === 'image' && f.header.mediaUrl ? (
                <img src={f.header.mediaUrl} alt="" />
              ) : (
                <span className="faint">{t(`whatsapp.builder.headerKind.${f.header.format}`)}</span>
              )}
            </div>
          )}
          <p dir="auto">{body || <span className="faint">{t('whatsapp.builder.bodyPlaceholder')}</span>}</p>
          {f.footer && (
            <small className="faint" dir="auto">
              {f.footer}
            </small>
          )}
        </div>
        {(f.buttons ?? []).map((b, i) => (
          <div key={i} className="wa-bubble-button" dir="auto">
            {b.type === 'url' ? (
              <ExternalLink size={13} />
            ) : b.type === 'phone' ? (
              <Phone size={13} />
            ) : (
              <Reply size={13} />
            )}
            {b.text || t(`whatsapp.builder.buttonKind.${b.type}`)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------------------------- library

function LibraryDialog({
  accountId,
  existing,
  onUse,
  onClose,
}: {
  accountId: string;
  existing: Template[];
  onUse: (p: TemplateInput) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['wa-library'],
    queryFn: () => api<{ items: LibraryItem[] }>('/v1/whatsapp/library'),
  });
  const have = new Set(existing.map((x) => `${x.name}:${x.language}`));
  const missing = (q.data?.items ?? []).filter((x) => !have.has(`${x.name}:${x.language}`));
  // One click: submit every library template not yet on this number.
  const all = useMutation({
    mutationFn: async () => {
      for (const x of missing)
        await api(`/v1/whatsapp/${accountId}/templates`, {
          method: 'POST',
          body: JSON.stringify({ ...x, use: undefined }),
        });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['wa-templates', accountId] });
      void qc.invalidateQueries({ queryKey: ['whatsapp'] });
    },
    onSuccess: onClose,
  });
  return (
    <Dialog
      open
      wide
      title={t('whatsapp.library.title')}
      onClose={onClose}
      onSubmit={() => all.mutate()}
      testId="wa-library-dialog"
      footer={
        <DialogButtons
          onClose={onClose}
          busy={all.isPending}
          disabled={!missing.length}
          label={t('whatsapp.library.submitAll', { count: missing.length })}
        />
      }
    >
      <p className="faint" style={{ marginTop: 0 }}>
        {t('whatsapp.library.hint')}
      </p>
      {q.isLoading && <Loading />}
      {all.isError && <ErrorState error={all.error} />}
      <ul className="list-plain wa-library">
        {(q.data?.items ?? []).map((x) => (
          <li key={`${x.name}:${x.language}`}>
            <div className="wa-template-head">
              <strong className="mono">{x.name}</strong>
              <span className="badge badge-neutral mono">{x.language}</span>
              <span className={`badge ${x.category === 'marketing' ? 'badge-warning' : 'badge-neutral'}`}>
                {t(`whatsapp.category.${x.category}`)}
              </span>
              <span className="grow" />
              {have.has(`${x.name}:${x.language}`) ? (
                <span className="faint">{t('whatsapp.library.added')}</span>
              ) : (
                <button className="btn btn-sm" onClick={() => onUse(x)}>
                  {t('whatsapp.library.use')}
                </button>
              )}
            </div>
            <p className="faint" dir="auto" style={{ margin: '0.25rem 0 0' }}>
              {x.body}
            </p>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}
